import { InlineNotice } from "@rosc/ui/v32/notice";
import { TriangleAlert, WifiOff } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";

/**
 * Shown while the live transcript reconnects after a lost connection. The
 * recording itself never stopped, which is what the doctor needs to know.
 */
export const LiveTranscriptPausedNotice = ({
  inline = false,
}: {
  /** Inside the v3.2 recorder, under its top bar (not above the page). */
  inline?: boolean;
} = {}): JSX.Element => {
  const { t } = useTranslation();
  const text = t("components.recorderInner.livePaused");
  // Inside the v3.2 recorder it is always the v3.2 notice.
  return inline || AppConfig.enableAppRedesign ? (
    <InlineNotice
      className={inline ? "font-v3" : "mx-3 mt-3 self-stretch font-v3"}
      data-testid="live-transcript-paused"
      icon={<WifiOff />}
      role="status"
      tone="warn"
    >
      {text}
    </InlineNotice>
  ) : (
    <p
      className="text-sm text-center"
      data-testid="live-transcript-paused"
      role="status"
    >
      {text}
    </p>
  );
};

/**
 * The v3.2 recorder's notice slot, under its top bar: the live transcript
 * notice first (it is about what is happening now), then why the note will
 * be written from the full recording, 8px apart.
 */
export const RecorderNotices = ({
  livePaused,
  recoveryText,
}: {
  livePaused: boolean;
  recoveryText: string | null;
}): JSX.Element => (
  <div className="flex flex-col gap-2">
    {livePaused && <LiveTranscriptPausedNotice inline />}
    {recoveryText && (
      <InlineNotice
        className="font-v3"
        data-testid="recorder-recovery-notice"
        icon={<TriangleAlert />}
        role="status"
        tone="warn"
      >
        {recoveryText}
      </InlineNotice>
    )}
  </div>
);
